import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import {
  useRegisterMutation,
  useUpdateMutation,
} from '../../app/services/maintenanceTypes';
import {
  fetchMaintenanceType,
  fetchMaintenanceTypes,
} from '../../features/maintenanceTypes/maintenanceTypesSlice';
import { commonTextFieldStyles } from '../../utils/textFieldStyles';
import { iconButtonStyles } from '../../utils/iconButtonStyles';
import { MODAL_STYLES } from '../../utils/modalStyles';
import ModalComponent from '../ModalComponent';
import ConfirmDiscardDialog from '../ConfirmDiscardDialog';
import { useUnsavedChangesWarning } from '../../hooks/useUnsavedChangesWarning';
import { useTranslation } from 'react-i18next';

import {
  Checkbox,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  TextField,
  Typography,
  Tooltip,
} from '@mui/material';
import {
  ModalDialogActions,
  ModalButton,
  ModalCloseButton,
  ModalAddButton,
} from '../ModalComponents';
import EditIcon from '@mui/icons-material/Edit';

import styles from '../ModalComponent/modalStyles.module.css';
import { validateFormData, getValidationErrorMessage } from '../../helpers';
import { postMaintenanceTypeSchema } from '../../schemas';

const initialForm = {
  name: '',
  description: '',
  is_external_service: false,
};

export default function ModalAddMaintenanceType({ id, type }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(initialForm);
  const [errorResponse, setErrorResponse] = useState('');
  const [isDirty, setIsDirty] = useState(false);
  const [registerType, { error: registerError }] = useRegisterMutation();
  const [updateType, { error: updateError }] = useUpdateMutation();

  const [formValidationErrors, setFormValidationErrors] = useState(null);
  const nameError = getValidationErrorMessage(formValidationErrors, 'name');
  const descrError = getValidationErrorMessage(
    formValidationErrors,
    'description',
  );

  const dispatch = useDispatch();
  const { t } = useTranslation();

  const { guardClose, confirmProps } = useUnsavedChangesWarning(isDirty, open);

  const handleOpen = () => setOpen(true);
  const doClose = () => {
    setOpen(false);
    setForm(initialForm);
    setFormValidationErrors(null);
    setErrorResponse('');
    setIsDirty(false);
  };
  const handleClose = () => guardClose(doClose);

  useEffect(() => {
    if (id && open) {
      (async () => {
        try {
          const response = await dispatch(fetchMaintenanceType(id));
          setForm({
            name: response.payload?.name || '',
            description: response.payload?.description || '',
            is_external_service: !!response.payload?.is_external_service,
          });
        } catch (err) {
          console.error('Error fetching maintenance type:', err);
        }
      })();
    }
  }, [id, open, dispatch]);

  useEffect(() => {
    const err = registerError || updateError;
    if (err) {
      setErrorResponse(err?.data?.message || 'Request failed');
    }
  }, [registerError, updateError]);

  const handleChange = (e) => {
    setErrorResponse('');
    setIsDirty(true);
    const { name, value, type: inputType, checked } = e.target;
    setForm({
      ...form,
      [name]: inputType === 'checkbox' ? checked : value,
    });
  };

  const handleSubmit = async () => {
    const validationErrors = validateFormData(t, postMaintenanceTypeSchema, form);
    if (validationErrors) {
      setFormValidationErrors(validationErrors);
      return;
    }
    try {
      if (id) {
        await updateType({ id, form }).unwrap();
      } else {
        await registerType(form).unwrap();
      }
      doClose();
      dispatch(fetchMaintenanceTypes());
    } catch (err) {
      console.error('Error saving maintenance type:', err);
    }
  };

  return (
    <div>
      {type === 'add' ? (
        <ModalAddButton onClick={handleOpen} />
      ) : (
        <Tooltip title={t('generic.edit')} placement="top">
          <IconButton color="primary" onClick={handleOpen} style={iconButtonStyles}>
            <EditIcon />
          </IconButton>
        </Tooltip>
      )}
      <ModalComponent open={open} handleClose={handleClose}>
        <div className={styles.modal}>
          <DialogTitle sx={MODAL_STYLES.dialogTitle}>
            {type === 'add'
              ? t('modals.createMaintenanceType')
              : t('modals.updateMaintenanceType')}
          </DialogTitle>
          <ModalCloseButton onClick={handleClose} />
          <DialogContent dividers sx={MODAL_STYLES.dialogContent}>
            <div style={MODAL_STYLES.formContainer}>
              <TextField
                label={t('generic.name')}
                variant="outlined"
                value={form.name}
                name="name"
                onChange={handleChange}
                fullWidth
                error={!!nameError}
                helperText={nameError ?? ' '}
                sx={commonTextFieldStyles}
              />
              <TextField
                label={t('generic.description')}
                variant="outlined"
                value={form.description}
                name="description"
                onChange={handleChange}
                fullWidth
                error={!!descrError}
                helperText={descrError ?? ' '}
                sx={commonTextFieldStyles}
              />

              <FormControlLabel
                control={
                  <Checkbox
                    name="is_external_service"
                    checked={!!form.is_external_service}
                    onChange={handleChange}
                  />
                }
                label={t('generic.isExternalService')}
              />

              {errorResponse &&
                (Array.isArray(errorResponse) ? (
                  errorResponse.map((item, i) => (
                    <Typography key={i} color="error">
                      {item}
                    </Typography>
                  ))
                ) : (
                  <Typography color="error">{errorResponse}</Typography>
                ))}
            </div>
          </DialogContent>
          <ModalDialogActions>
            <ModalButton variant="outlined" color="primary" onClick={handleClose}>
              {t('modals.cancel')}
            </ModalButton>
            <ModalButton variant="contained" color="primary" onClick={handleSubmit}>
              {type === 'add' ? t('modals.create') : t('modals.update')}
            </ModalButton>
          </ModalDialogActions>
        </div>
      </ModalComponent>
      <ConfirmDiscardDialog {...confirmProps} />
    </div>
  );
}
